import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getMetrics, type Metrics } from '../api/client';
import { subDays } from 'date-fns';

const VERDICT_COLORS: Record<string, string> = {
  ALLOW: '#22c55e',
  CHALLENGE: '#eab308',
  BLOCK: '#ef4444',
  THROTTLE: '#f97316',
};

export default function MetricsPage() {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('24h');

  useEffect(() => {
    loadMetrics();
  }, [timeRange]);

  async function loadMetrics() {
    setLoading(true);
    setError(null);

    const now = new Date();
    let from: Date;

    switch (timeRange) {
      case '1h':
        from = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '24h':
        from = subDays(now, 1);
        break;
      case '7d':
        from = subDays(now, 7);
        break;
      case '30d':
        from = subDays(now, 30);
        break;
      default:
        from = subDays(now, 1);
    }

    const result = await getMetrics(from.toISOString(), now.toISOString());

    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setMetrics(result.data);
    }

    setLoading(false);
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg">
          Error loading metrics: {error}
        </div>
      </div>
    );
  }

  const verdictData = metrics
    ? Object.entries(metrics.by_verdict).map(([name, value]) => ({
        name,
        value,
        color: VERDICT_COLORS[name] || '#6b7280',
      }))
    : [];

  const routeData = metrics
    ? Object.entries(metrics.by_route_class).map(([name, value]) => ({
        name,
        value,
      }))
    : [];

  const countryData = metrics
    ? Object.entries(metrics.by_country)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="select w-40"
        >
          <option value="1h">Last 1 hour</option>
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="text-gray-400 text-sm mb-1">Total Events</div>
          <div className="text-3xl font-bold">{metrics?.total_events.toLocaleString() || 0}</div>
        </div>
        <div className="card">
          <div className="text-gray-400 text-sm mb-1">Avg Risk Score</div>
          <div className="text-3xl font-bold">{metrics?.avg_score.toFixed(1) || 0}</div>
        </div>
        <div className="card">
          <div className="text-gray-400 text-sm mb-1">Blocked</div>
          <div className="text-3xl font-bold text-red-400">
            {metrics?.by_verdict.BLOCK?.toLocaleString() || 0}
          </div>
        </div>
        <div className="card">
          <div className="text-gray-400 text-sm mb-1">Challenged</div>
          <div className="text-3xl font-bold text-yellow-400">
            {metrics?.by_verdict.CHALLENGE?.toLocaleString() || 0}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Verdicts pie chart */}
        <div className="card">
          <h2 className="card-header">Verdicts Distribution</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={verdictData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {verdictData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Routes bar chart */}
        <div className="card">
          <h2 className="card-header">Events by Route</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={routeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top countries */}
      <div className="card">
        <h2 className="card-header">Top Countries</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={countryData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis type="number" stroke="#9ca3af" fontSize={12} />
              <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} width={60} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
